(() => {
  const roots = new WeakMap();
  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim();
  const keyPart = (value) => normalize(value).toLocaleLowerCase();

  const visible = (element) => {
    for (let current = element; current; current = current.parentElement) {
      if (current.hidden || current.getAttribute('aria-hidden') === 'true') return false;
      const style = window.getComputedStyle(current);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
    }
    return true;
  };

  const ingredientText = (item) => {
    const copy = item.cloneNode(true);
    copy.querySelectorAll('.select_div, .recipe-reader__checkbox').forEach((node) => node.remove());
    const parts = copy.querySelector('.rk_ingredient__item');
    if (parts) {
      return normalize([
        parts.querySelector('.rk_ingredient__item--quantity')?.textContent,
        parts.querySelector('.rk_ingredient__item--unit')?.textContent,
        parts.querySelector('.rk_ingredient__item--ingredient')?.textContent,
      ].join(' '));
    }
    return normalize(copy.textContent);
  };

  const savedKeys = (storageKey) => {
    try {
      const data = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      return new Set(Array.isArray(data) ? data.filter((item) => typeof item === 'string') : []);
    } catch (_) {
      return new Set();
    }
  };

  const saveKeys = (storageKey, keys) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify([...keys]));
    } catch (_) {
      // Readers can still check ingredients when storage is unavailable.
    }
  };

  const copyStylesForPrint = (root, cleanup) => {
    const source = root.querySelector('link[data-recipe-reader-styles]');
    if (!source?.href || document.head.querySelector('link[data-recipe-reader-styles-copy]')) return;
    const clone = source.cloneNode(true);
    clone.setAttribute('data-recipe-reader-styles-copy', '');
    document.head.appendChild(clone);
    cleanup.push(() => clone.remove());
  };

  const installAnchor = (root, region, name, cleanup) => {
    if (!region) return null;
    const heading = region.matches?.('h1, h2, h3, h4, h5, h6')
      ? region
      : region.querySelector('h1, h2, h3, h4, h5, h6');
    if (heading) {
      const originalId = heading.id;
      const originalTabindex = heading.getAttribute('tabindex');
      if (!heading.id) heading.id = `${root.id || 'RecipeReader'}-${name}`;
      if (originalTabindex == null) heading.setAttribute('tabindex', '-1');
      heading.classList.add('recipe-reader__jump-heading');
      cleanup.push(() => {
        heading.classList.remove('recipe-reader__jump-heading');
        if (!originalId) heading.removeAttribute('id');
        if (originalTabindex == null) heading.removeAttribute('tabindex');
      });
      return heading.id;
    }
    if (region.id && document.getElementById(region.id) === region) return region.id;
    const anchor = document.createElement('span');
    anchor.id = `${root.id || 'RecipeReader'}-${name}`;
    anchor.className = 'recipe-reader__anchor';
    anchor.setAttribute('aria-hidden', 'true');
    region.prepend(anchor);
    cleanup.push(() => anchor.remove());
    return anchor.id;
  };

  const synchronizeCurrentIngredientNames = (card) => {
    const widget = card.querySelector('recipe-kit > .rk_widget_root');
    if (!widget) return;
    widget.querySelectorAll('div.rk_meta[aria-label]').forEach((meta) => {
      if ((meta.getAttribute('aria-label') === 'Preparation time' && normalize(meta.textContent) === 'Prep Time') ||
          (meta.getAttribute('aria-label') === 'Cooking time' && normalize(meta.textContent) === 'Cook Time')) {
        meta.removeAttribute('aria-label');
      }
    });
    widget.querySelectorAll('.rk_serving_adjuster[role="group"][aria-label="Adjust serving size"] > .rk_serving_count[aria-label]').forEach((count) => {
      if (normalize(count.textContent)) count.removeAttribute('aria-label');
    });
    widget.querySelectorAll('button.rk_unit_btn[role="radio"]').forEach((button) => {
      const visibleUnits = normalize(button.textContent);
      const purpose = (button.getAttribute('aria-label') || '').match(/(?:Switch to )?(US|Metric) units/i);
      if (!visibleUnits || !purpose) return;
      const name = `${purpose[1].toUpperCase() === 'US' ? 'US' : 'Metric'} units: ${visibleUnits}`;
      if (button.getAttribute('aria-label') !== name) button.setAttribute('aria-label', name);
    });
    widget.querySelectorAll('.rk_shoppable a.rk_has_link').forEach((link) => {
      const label = normalize(link.textContent);
      if (!label) return;
      const name = link.target === '_blank' ? `${label} (opens in a new tab)` : label;
      if (link.getAttribute('aria-label') !== name) link.setAttribute('aria-label', name);
    });
    widget.querySelectorAll('label.rk_ingredient_label > input.rk_checkbox[type="checkbox"]').forEach((checkbox) => {
      const content = checkbox.parentElement.querySelector('.rk_ingredient_content');
      if (!content) return;
      const visibleText = (node) => {
        if (node.nodeType === 3) return node.textContent;
        if (node.nodeType !== 1 || !visible(node) ||
            node.matches('.select_div, .rk_print_hide, input, button, select, textarea, [aria-hidden="true"]')) return '';
        if (node.tagName === 'BR') return ' ';
        const text = Array.from(node.childNodes, visibleText).join('');
        const display = window.getComputedStyle(node).display;
        return /^(block|flex|grid|list-item|table)/.test(display) ? ` ${text} ` : text;
      };
      // A still-hidden hydrated widget has no current visible ingredient text.
      // Its later visibility change is observed below before assigning a name.
      const label = normalize(visibleText(content));
      if (!label) return;
      const name = `Mark ${label} as complete`;
      if (checkbox.getAttribute('aria-label') !== name) checkbox.setAttribute('aria-label', name);
    });
  };

  const enhanceIngredients = (root, signal, cleanup) => {
    const reset = root.querySelector('[data-reader-reset]');
    if (root.dataset.clickToCheck === 'true') {
      if (reset) reset.hidden = true;
      return;
    }
    const storageKey = `recipe-reader:v1:${root.dataset.articleId || root.id}`;
    const checked = savedKeys(storageKey);
    const occurrences = new Map();
    const entries = [];
    const groups = root.querySelectorAll('.rk_ingredients #recipe-ingredients');
    groups.forEach((group) => {
      let heading = '';
      Array.from(group.children).forEach((child) => {
        if (child.matches('.rk_group_heading')) {
          heading = keyPart(child.textContent);
          return;
        }
        if (!child.matches('li')) return;
        const label = ingredientText(child);
        if (!label) return;
        const base = `${heading}|${keyPart(label)}`;
        const occurrence = occurrences.get(base) || 0;
        occurrences.set(base, occurrence + 1);
        const key = `${base}|${occurrence}`;
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'recipe-reader__checkbox';
        checkbox.setAttribute('aria-label', `Check off ${label}`);
        checkbox.dataset.readerKey = key;
        checkbox.checked = checked.has(key);
        child.prepend(checkbox);
        child.classList.add('recipe-reader__ingredient');
        child.classList.toggle('is-checked', checkbox.checked);
        entries.push({ checkbox, item: child, key });
        checkbox.addEventListener('change', () => {
          child.classList.toggle('is-checked', checkbox.checked);
          if (checkbox.checked) checked.add(key);
          else checked.delete(key);
          saveKeys(storageKey, checked);
          if (reset) reset.hidden = checked.size === 0;
        }, { signal });
      });
    });
    const currentKeys = new Set(entries.map(({ key }) => key));
    let pruned = false;
    for (const key of checked) {
      if (currentKeys.has(key)) continue;
      checked.delete(key);
      pruned = true;
    }
    if (pruned) saveKeys(storageKey, checked);
    if (reset) {
      reset.hidden = checked.size === 0;
      reset.addEventListener('click', () => {
        checked.clear();
        entries.forEach(({ checkbox, item }) => {
          checkbox.checked = false;
          item.classList.remove('is-checked');
        });
        saveKeys(storageKey, checked);
        reset.hidden = true;
        (entries[0]?.checkbox || root.querySelector('[data-reader-jump="ingredients"]'))?.focus();
      }, { signal });
    }
    cleanup.push(() => entries.forEach(({ checkbox, item }) => {
      checkbox.remove();
      item.classList.remove('recipe-reader__ingredient', 'is-checked');
    }));
  };

  const enhance = (root) => {
    const pending = roots.get(root);
    if (pending && !pending.pending) return;
    const card = root.querySelector('[data-reader-card]');
    const currentWidget = card?.dataset.readerRenderer === 'current';
    const recipe = card?.querySelector(currentWidget ? 'recipe-kit > .rk_widget_root' : '#rk');
    const tools = root.querySelector('[data-reader-tools]');
    const print = tools?.querySelector('[data-reader-print]');
    if (!recipe || !tools || !print) return;
    const ingredients = recipe.querySelector(currentWidget ? '#rk-ingredients' : '.rk_ingredients');
    const directions = recipe.querySelector(currentWidget ? '#rk-directions' : '.rk_directions');
    // The current widget replaces its server fallback during hydration. Wait for its own IDs.
    if (currentWidget && (!ingredients || !directions)) return;

    const controller = new AbortController();
    const cleanup = [];
    const state = { controller, cleanup, observer: null };
    pending?.observer?.disconnect();
    roots.set(root, state);
    const { signal } = controller;

    const currentTargets = new WeakSet();
    for (const [name, region] of [['ingredients', ingredients], ['directions', directions]]) {
      const link = tools.querySelector(`[data-reader-jump="${name}"]`);
      const id = installAnchor(root, region, name, cleanup);
      if (currentWidget) currentTargets.add(region);
      if (link && id) {
        link.href = `#${id}`;
        link.hidden = false;
      } else if (link) {
        link.hidden = true;
      }
    }

    if (currentWidget && window.MutationObserver) {
      // The vendor replaces its root during hydration and can replace links when units or servings change.
      const refresh = () => {
        const widget = card.querySelector('recipe-kit > .rk_widget_root');
        for (const [name, selector] of [['ingredients', '#rk-ingredients'], ['directions', '#rk-directions']]) {
          const target = widget?.querySelector(selector);
          if (!target || currentTargets.has(target)) continue;
          currentTargets.add(target);
          const id = installAnchor(root, target, name, cleanup);
          const link = tools.querySelector(`[data-reader-jump="${name}"]`);
          if (link && id) link.href = `#${id}`;
        }
        synchronizeCurrentIngredientNames(card);
      };
      let observedWidget = null;
      const resizeObserver = window.ResizeObserver ? new window.ResizeObserver(refresh) : null;
      if (resizeObserver) resizeObserver.observe(card);
      const observeWidget = () => {
        const widget = card.querySelector('recipe-kit > .rk_widget_root');
        if (resizeObserver && widget && widget !== observedWidget) {
          if (observedWidget) resizeObserver.unobserve(observedWidget);
          resizeObserver.observe(widget);
          observedWidget = widget;
        }
      };
      const refreshWithVisibility = () => {
        observeWidget();
        refresh();
      };
      const observer = new MutationObserver(refreshWithVisibility);
      observer.observe(card, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['aria-label', 'target', 'class', 'style', 'hidden', 'aria-hidden'],
      });
      cleanup.push(() => { observer.disconnect(); resizeObserver?.disconnect(); });
      window.addEventListener('resize', refreshWithVisibility, { signal });
      refreshWithVisibility();
    }

    if (!currentWidget) enhanceIngredients(root, signal, cleanup);
    print.addEventListener('click', () => {
      const nativePrint = currentWidget && card.querySelector('recipe-kit > .rk_widget_root button.rk_action_btn.rk_print');
      // Keep forwarding synchronous so the vendor print window retains the user's gesture.
      if (nativePrint) nativePrint.click();
      else window.print();
    }, { signal });
    const header = document.getElementById('SiteHeader');
    let offsetFrame = null;
    const updateOffsets = () => {
      offsetFrame = null;
      const headerHeight = Math.max(0, Math.ceil(header?.getBoundingClientRect().height || 0));
      const toolbarHeight = root.classList.contains('is-cooking')
        ? Math.max(0, Math.ceil(tools.getBoundingClientRect().height || 0))
        : 0;
      root.style.setProperty('--reader-header-height', `${headerHeight}px`);
      root.style.setProperty('--reader-jump-offset', `${headerHeight + toolbarHeight + 24}px`);
    };
    const scheduleOffsets = () => {
      if (offsetFrame != null) return;
      offsetFrame = window.requestAnimationFrame(updateOffsets);
    };
    window.addEventListener('resize', scheduleOffsets, { signal });
    if (window.ResizeObserver) {
      const observer = new window.ResizeObserver(scheduleOffsets);
      if (header) observer.observe(header);
      observer.observe(tools);
      cleanup.push(() => observer.disconnect());
    }
    cleanup.push(() => {
      if (offsetFrame != null) window.cancelAnimationFrame(offsetFrame);
      root.style.removeProperty('--reader-header-height');
      root.style.removeProperty('--reader-jump-offset');
    });
    const cook = tools.querySelector('[data-reader-cook]');
    if (cook) {
      if (currentWidget) cook.textContent = 'Focus mode';
      const originalLabel = normalize(cook.textContent) || 'Cook mode';
      cook.addEventListener('click', () => {
        const active = root.classList.toggle('is-cooking');
        document.documentElement.classList.toggle('recipe-reader-cooking', active);
        cook.setAttribute('aria-pressed', String(active));
        cook.textContent = active ? `Exit ${originalLabel.toLocaleLowerCase()}` : originalLabel;
        updateOffsets();
        if (active && typeof card.scrollIntoView === 'function') {
          const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
          card.scrollIntoView({ behavior: reduced ? 'instant' : 'smooth', block: 'start' });
        }
      }, { signal });
      cleanup.push(() => document.documentElement.classList.remove('recipe-reader-cooking'));
    }

    const title = keyPart(root.dataset.articleTitle);
    const cardTitle = keyPart(recipe.querySelector('.rk_heading')?.textContent);
    if (title && cardTitle === title) {
      const externalTitle = Array.from(document.querySelectorAll('h1')).find((heading) =>
        !card.contains(heading) && keyPart(heading.textContent) === title && visible(heading));
      if (externalTitle) root.setAttribute('data-reader-duplicate-title', 'true');
    }
    copyStylesForPrint(root, cleanup);
    tools.hidden = false;
    root.setAttribute('data-reader-ready', 'true');
    updateOffsets();
  };

  const start = (scope = document) => {
    const candidates = [];
    if (scope.matches?.('[data-recipe-reader]')) candidates.push(scope);
    scope.querySelectorAll?.('[data-recipe-reader]').forEach((root) => candidates.push(root));
    candidates.forEach((root) => {
      if (roots.has(root)) return;
      enhance(root);
      if (roots.has(root) || !window.MutationObserver) return;
      const card = root.querySelector('[data-reader-card]');
      if (!card) return;
      const observer = new MutationObserver(() => {
        enhance(root);
        if (roots.has(root) && !roots.get(root).pending) observer.disconnect();
      });
      observer.observe(card, { childList: true, subtree: true });
      roots.set(root, { observer, pending: true });
    });
  };

  const stop = (scope) => {
    const candidates = [];
    if (scope.matches?.('[data-recipe-reader]')) candidates.push(scope);
    scope.querySelectorAll?.('[data-recipe-reader]').forEach((root) => candidates.push(root));
    candidates.forEach((root) => {
      const state = roots.get(root);
      if (!state) return;
      state.observer?.disconnect();
      state.controller?.abort();
      state.cleanup?.forEach((fn) => fn());
      root.classList.remove('is-cooking');
      root.removeAttribute('data-reader-ready');
      root.removeAttribute('data-reader-duplicate-title');
      roots.delete(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => start(), { once: true });
  } else {
    start();
  }
  document.addEventListener('shopify:section:load', (event) => start(event.target));
  document.addEventListener('shopify:section:unload', (event) => stop(event.target));
})();
